import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { RoutingService } from '@spartacus/core';
import { Subscription, throwError } from 'rxjs';
import { DataLayerService } from 'src/app/shared/services/data-layer.service';
import { AccountOverviewService, OfferBanner } from '../../account-overview.service';
import { ReportsService } from '../../../reports/reports.service';
import { UserAccountFacade } from '@spartacus/user/account/root';
import { map, switchMap, take } from 'rxjs/operators';

@Component({
    selector: 'app-offers-banner',
    templateUrl: './offers-banner.component.html',
    styleUrls: ['./offers-banner.component.scss'],
    standalone: false
})
export class OffersBannerComponent implements OnInit, OnDestroy {
  offersEligible = false;
  offerAccepted = false;
  isBannerVisible = true;
  banners: OfferBanner[] = [];
  private closedBannerIndices = new Set<number>();
  private subs: Subscription[] = [];

  constructor(
    private readonly acService: AccountOverviewService,
    private readonly cdr: ChangeDetectorRef,
    private readonly _dataLayerService: DataLayerService,
    private readonly router: RoutingService,
    private readonly reportsService: ReportsService,
    private readonly userAccountFacade: UserAccountFacade
  ) {}

  ngOnInit(): void {
    //Subscribe to eligibility
    this.subs.push(
      this.acService.offerEligibility$.subscribe(res => {
        if (res) {
          this.offersEligible = res.offersEligible;
          this.offerAccepted = res.offersAccepted;
          this.cdr.detectChanges();
        }
      })
    );

    //Subscribe to banners
    this.subs.push(
      this.acService.offerBanners$.subscribe(res => {
        this.banners = res?.banners ?? [];
        // Reset closed indices when banners change
        this.closedBannerIndices.clear();
        this.cdr.detectChanges();
      })
    );
  }

  onBannerDisplayed(banner: OfferBanner): void {
    this._dataLayerService.addtoDataLayer('banner_displayed', {
      promotion_name: 'app-offers-banner',
      creative_slot: 'body_top',
      headline: this.getHeadline(banner),
    });
  }

  onBannerClick(event: MouseEvent, banner: OfferBanner): void {
    const target = event.target as HTMLElement;
    const clickedClose = target.closest('button[aria-label="close offers banner"]');
    const clickedCTA = target.closest('.btn-primary');
    const clickedSecondaryCTA = target.closest('.btn-secondary');

    // If user clicked somewhere on the banner *that isn't* close or CTA,
    // treat it like a CTA click to the banner's link.
    if (!clickedClose && !clickedCTA && !clickedSecondaryCTA) {
      const meta = this.resolvePrimaryLink(banner);
      this.handleNavigation(meta);
    }
  }

  onViewOffer(banner: OfferBanner, event?: MouseEvent): void {
    // Prevent <a> default nav so we can handle internal vs external consistently
    event?.preventDefault();
    event?.stopPropagation();

    const meta = this.resolvePrimaryLink(banner);

    this._dataLayerService.addtoDataLayer('banner_link_clicked', {
      promotion_name: 'app-offers-banner',
      creative_slot: 'body_top',
      headline: this.getHeadline(banner),
      cta_label: this.getPrimaryCtaLabel(banner),
      cta_link: meta.link,
    });

    this.handleNavigation(meta);
  }

  onSecondaryCtaClick(banner: OfferBanner, event?: MouseEvent): void {
    event?.preventDefault();
    event?.stopPropagation();

    const meta = this.resolveSecondaryLink(banner);

    this._dataLayerService.addtoDataLayer('banner_secondary_link_clicked', {
      promotion_name: 'app-offers-banner',
      creative_slot: 'body_top',
      headline: this.getHeadline(banner),
      cta_label: banner?.secondaryCtaLabel,
      cta_link: meta.link,
    });

    this.handleNavigation(meta);
  }

  closeBanner(event?: MouseEvent, bannerIndex?: number): void {
    event?.stopPropagation();
    if (bannerIndex !== undefined) {
      this.closedBannerIndices.add(bannerIndex);
      this.cdr.detectChanges();
    }
  }

  isBannerClosed(index: number): boolean {
    return this.closedBannerIndices.has(index);
  }

  getHeadline(banner: OfferBanner): string {
    const headline = this.isAcceptedState(banner)
      ? (banner.postAcceptanceHeadline || banner.headline || '')
      : (banner.headline || '');
    return headline.trim();
  }

  getSubHeadline(banner: OfferBanner): string | undefined {
    const subHeadline = this.isAcceptedState(banner)
      ? (banner.postAcceptanceSubHeadline || banner.subHeadline || '')
      : (banner.subHeadline || '');
    const trimmed = subHeadline.trim();
    return trimmed ? trimmed : undefined;
  }

  getPrimaryCtaLabel(banner: OfferBanner): string {
    if (this.useSecondaryAsPrimary(banner) && banner.secondaryCtaLabel) {
      return banner.secondaryCtaLabel.trim();
    }
    return (banner.ctaLabel || 'View Offer').trim();
  }

  getPrimaryCtaLink(banner: OfferBanner): string {
    return this.resolvePrimaryLink(banner).link;
  }

  showSecondaryCta(banner: OfferBanner): boolean {
    if (this.useSecondaryAsPrimary(banner)) {
      return false;
    }

    if (!banner?.secondaryCtaLabel && !banner?.secondaryCtaLink) {
      return false;
    }

    return !this.isAcceptedState(banner)
      && banner.secondaryCtaAcceptanceOnly === false
      && Boolean(banner.secondaryCtaLabel || banner.secondaryCtaLink);
  }

  private isAcceptedState(banner: OfferBanner): boolean {
    if (!banner) {
      return false;
    }
    const offerAccepted = Boolean(banner.accepted);
    const globalAccepted = !banner.offer && this.offerAccepted;
    return offerAccepted || globalAccepted;
  }

  private useSecondaryAsPrimary(banner: OfferBanner): boolean {
    return this.isAcceptedState(banner)
      && Boolean(banner.secondaryCtaLabel || banner.secondaryCtaLink);
  }

  private handleNavigation(meta: LinkMeta): void {
    if (meta.reportCode) {
      this.launchReport(meta.reportCode);
      return;
    }
    this.navigateToLink(meta.link);
  }

  private navigateToLink(rawLink: string): void {
    const link = (rawLink || '/offers').trim();
    // If it looks external (http/https or protocol-relative), do a full navigation.
    const isExternal = /^(https?:)?\/\//i.test(link);

    if (isExternal) {
      // External: leave the SPA
      window.location.href = link;
    } else {
      // Internal path or Spartacus route alias: use router
      this.router.goByUrl(link.startsWith('/') ? link : `/${link}`);
    }
  }

  ngOnDestroy(): void {
    this.subs.forEach(s => s.unsubscribe());
  }

  private resolvePrimaryLink(banner: OfferBanner): LinkMeta {
    const useSecondary = this.useSecondaryAsPrimary(banner);
    const raw = useSecondary ? banner.secondaryCtaLink : banner.ctaLink;
    return this.buildLinkMeta(raw, '/offers');
  }

  private resolveSecondaryLink(banner: OfferBanner): LinkMeta {
    const raw = banner.secondaryCtaLink;
    const fallback = banner.ctaLink || '/offers';
    return this.buildLinkMeta(raw, fallback);
  }

  private buildLinkMeta(rawLink: string | null | undefined, fallback: string): LinkMeta {
    const trimmed = rawLink?.trim();
    const link = trimmed && trimmed.length > 0 ? trimmed : fallback;
    return {
      link,
      reportCode: this.extractReportCode(trimmed)
    };
  }

  private extractReportCode(link?: string | null): string | null {
    if (!link) {
      return null;
    }
    const match = link.match(/^\/report\/([^?]+)/i);
    return match ? match[1] : null;
  }

  private launchReport(reportCode: string): void {
    const targetWindow = window.open('', '_blank');

    const sub = this.ensureUserName()
      .pipe(
        switchMap((user) => {
          if (!user) {
            return throwError(() => new Error('Missing user id'));
          }
          return this.reportsService.getreportDetails(reportCode, user).pipe(take(1));
        })
      )
      .subscribe({
        next: (res) => {
          const targetUrl = res?.url ? decodeURIComponent(res.url) : '';
          if (targetUrl && targetWindow) {
            targetWindow.opener = null;
            targetWindow.location.href = targetUrl;
          } else {
            this.routeToReportsDashboard(targetWindow);
          }
        },
        error: () => {
          this.routeToReportsDashboard(targetWindow);
        }
      });
    this.subs.push(sub);
  }

  private ensureUserName() {
    return this.userAccountFacade.get().pipe(
      take(1),
      map((res) => res?.userName || res?.uid || '')
    );
  }

  private routeToReportsDashboard(windowRef?: Window | null): void {
    try {
      if (windowRef && !windowRef.closed) {
        windowRef.close();
      }
    } catch (error) {
      // ignore cross-origin close issues
    }

    // Defer navigation so we don't fight Angular's zone
    setTimeout(() => this.router.goByUrl('/reportDashboardPage'), 0);
  }
}

interface LinkMeta {
  link: string;
  reportCode?: string | null;
}
